/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { TrashIcon } from "@heroicons/react/24/outline";
import Avatar from "../../components/Avatar";
import { useLanguage } from "../../contexts/LanguageContext";
import { formatNumber } from "../dashboard/format";
import { buttonClass, cardClass } from "../../components/ui/styles";
import { Pill } from "../../components/ui/Pill";
import { displayName, formatAddedAt } from "./format";

const ROLE_TONE = { owner: "dark", admin: "indigo", member: "neutral" };

// t() hands back the key itself when a translation is missing; a pill reading
// "roles.owner" would be worse than the enum it replaced.
function roleLabel(t, role) {
  const key = `roles.${role}`;
  const label = t(key);
  return label === key ? role : label;
}

// One markup for every width: a table from md up, and below that each row
// stacks into a card (thead hidden, cells as blocks, the scans cell labelled).
export default function MembersTable({
  members,
  isSelf,
  showScans,
  showActions,
  pendingIds,
  onToggleRole,
  onToggleAccess,
  onRemove,
}) {
  const { t } = useLanguage();

  return (
    <section
      aria-label={t("team.columns.member")}
      className={`${cardClass} min-w-0 overflow-hidden`}
    >
      <table className="block w-full text-left text-sm md:table md:table-fixed">
        <thead className="hidden bg-gray-50 md:table-header-group">
          <tr className="text-xs font-semibold text-gray-600">
            <th scope="col" className="px-5 py-2.5">
              {t("team.columns.member")}
            </th>
            <th scope="col" className="w-[250px] px-3 py-2.5">
              {t("team.columns.roleAccess")}
            </th>
            {showScans && (
              <th scope="col" className="w-[110px] px-3 py-2.5 text-right">
                {t("team.columns.scans")}
              </th>
            )}
            {showActions && (
              <th scope="col" className="w-[360px] px-5 py-2.5 text-right">
                {t("team.columns.actions")}
              </th>
            )}
          </tr>
        </thead>
        <tbody className="block md:table-row-group">
          {members.map((member) => (
            <MemberRow
              key={member.id ?? `owner-${member.user?.id}`}
              member={member}
              self={isSelf(member)}
              showScans={showScans}
              showActions={showActions}
              pending={member.id != null && pendingIds.has(member.id)}
              onToggleRole={onToggleRole}
              onToggleAccess={onToggleAccess}
              onRemove={onRemove}
            />
          ))}
        </tbody>
      </table>
    </section>
  );
}

function MemberRow({
  member,
  self,
  showScans,
  showActions,
  pending,
  onToggleRole,
  onToggleAccess,
  onRemove,
}) {
  const { t, language } = useLanguage();
  const name = displayName(member);
  const isOwner = member.role === "owner";
  const scannerOnly =
    member.role === "member" && member.dashboard_access === false;
  const added = isOwner ? "" : formatAddedAt(member.created_at, language);

  return (
    <tr className="flex flex-col gap-3 border-t border-gray-100 px-4 py-4 first:border-t-0 md:table-row md:p-0 md:first:border-t">
      <td className="block md:table-cell md:px-5 md:py-3.5 md:align-middle">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar
            src={member.user?.avatar}
            name={name}
            className="h-10 w-10 text-sm"
          />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate font-semibold text-gray-900">
                {name}
              </span>
              {self && <Pill tone="neutral">{t("team.you")}</Pill>}
            </span>
            <span className="truncate text-[13px] text-gray-500">
              {member.user?.email}
            </span>
          </div>
        </div>
      </td>

      <td className="block md:table-cell md:px-3 md:py-3.5 md:align-middle">
        <div className="flex flex-wrap gap-1.5">
          <Pill tone={ROLE_TONE[member.role]} className="min-w-[72px]">
            {roleLabel(t, member.role)}
          </Pill>
          <Pill tone={scannerOnly ? "orange" : "green"}>
            {scannerOnly ? t("team.scannerOnly.badge") : t("team.access.full")}
          </Pill>
        </div>
        {added && (
          <span className="mt-1.5 block text-xs text-gray-600">
            {t("team.added", { when: added })}
          </span>
        )}
      </td>

      {showScans && (
        <td className="flex items-center justify-between md:table-cell md:px-3 md:py-3.5 md:text-right md:align-middle">
          <span className="text-[13px] text-gray-600 md:hidden">
            {t("team.columns.scans")}
          </span>
          <span className="font-semibold tabular-nums text-gray-900">
            {Number.isFinite(member.scans_30d)
              ? formatNumber(member.scans_30d, language)
              : "–"}
          </span>
        </td>
      )}

      {showActions && (
        <td className="block md:table-cell md:px-5 md:py-3.5 md:align-middle">
          <div className="flex flex-wrap items-center gap-1.5 md:justify-end">
            {isOwner ? (
              <span className="text-[13px] text-gray-500">
                {t("team.actions.ownerFixed")}
              </span>
            ) : self ? (
              <span className="text-[13px] text-gray-500">
                {t("team.actions.self")}
              </span>
            ) : (
              <>
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => onToggleRole(member)}
                  className={`${buttonClass.small} min-w-[128px]`}
                >
                  {member.role === "admin"
                    ? t("team.actions.makeMember")
                    : t("team.actions.makeAdmin")}
                </button>
                {member.role === "member" ? (
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => onToggleAccess(member)}
                    className={`${buttonClass.small} min-w-[128px]`}
                  >
                    {scannerOnly
                      ? t("team.actions.allowDashboard")
                      : t("team.actions.scannerOnly")}
                  </button>
                ) : (
                  <span
                    aria-hidden="true"
                    className="hidden min-w-[128px] md:inline-block"
                  />
                )}
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => onRemove(member)}
                  aria-label={t("team.actions.remove", { name })}
                  title={t("team.actions.remove", { name })}
                  className={buttonClass.dangerIcon}
                >
                  <TrashIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}
